import React, { useContext, useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import { getRutaReportServerList, UpdateGroup, UpdateReportServer } from '../../../Service/rutaReportServerService';
import { AuthContext } from '../../../Context/AuthContext';
import { Alert, Snackbar, Typography, Chip, CircularProgress } from '@mui/material';
import './ConfigRutaReportServer.css';
import EditReportServer from './EditReportServer';
import EscoDataGrid from '../../Controls/EscoGrid/EscoDataGrid';

const ConfigRutaReportServer = () => {
  const [reportes, setReportes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { tokenExpiration, getAccessToken } = useContext(AuthContext);
  const [alert, setAlert] = useState({ severity: 'info', message: '', open: false });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const rutaReportServer = () => {
    getAccessToken().then((token) => {
      setLoading(true);
      getRutaReportServerList(token)
        .then((data) => {
          const formattedData = data.map((row) => ({
            id: `${row.CodEntidad}-${row.Id}`,
            Reporte: row.Reporte,
            Group: row.Group,
            Descripcion: row.Descripcion || 'Sin descripción',
            Activo: row.Activo ?? 0,
          }));
          setReportes(formattedData);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    });
  };

  useEffect(() => {
    rutaReportServer();
  }, []);

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const handleSavePopup = async (updatedData) => {
    console.log(updatedData);
    const { id, Reporte, Group, Activo } = updatedData;
    const [codEntidad, rowId] = id.split('-');
    try {
      const token = await getAccessToken();
      const response = await UpdateReportServer(codEntidad, rowId, Reporte, Group, Activo, token);
      if (response.success) {
        const updatedRows = reportes.map((row) =>
          row.id === id ? { ...row, ...updatedData } : row
        );
        setReportes(updatedRows);
        setAlert({
          open: true,
          severity: 'success',
          message: 'Registro actualizado correctamente',
        });
      } else {
        setAlert({
          open: true,
          severity: 'error',
          message: `Error al actualizar la base de datos: ${response.message}`,
        });
      }
    } catch (error) {
      console.error('Error al guardar los datos:', error.message);
      setAlert({
        open: true,
        severity: 'error',
        message: 'Error al sincronizar los datos con el backend',
      });
    }
    setIsPopupOpen(false);
  };

  const handleUpdateGroup = async (codEntidad, rowId, reporte, activo, newGroupValue) => {
    try {
      const token = await getAccessToken();
      const response = await UpdateGroup(codEntidad, rowId, reporte, newGroupValue, activo, token);

      if (response.success) {
        setAlert({ severity: 'success', message: 'Ruta actualizada correctamente', open: true });
      } else {
        setAlert({ severity: 'error', message: `Error al actualizar la ruta: ${response.message}`, open: true });
      }
    } catch (error) {
      console.error('Error inesperado en handleUpdateGroup:', error.message);
      setAlert({ severity: 'error', message: 'Error inesperado al actualizar la ruta', open: true });
    }
  };

  const handleUpdateReportServer = async (codEntidad, rowId, reporte, activo, newGroupValue) => {
    try {
      const token = await getAccessToken();
      const response = await UpdateReportServer(codEntidad, rowId, reporte, newGroupValue, activo, token);

      if (response.success) {
        setAlert({ severity: 'success', message: 'Ruta actualizada correctamente', open: true });
      } else {
        setAlert({ severity: 'error', message: `Error al actualizar la ruta: ${response.message}`, open: true });
      }
    } catch (error) {
      console.error('Error inesperado en handleUpdateReportServer:', error.message);
      setAlert({ severity: 'error', message: 'Error inesperado al actualizar la ruta', open: true });
    }
  };

  const handleProcessRowUpdate = async (newRow) => {
    const { id, Reporte: reporte, Group: newGroupValue, Activo: activo } = newRow;
    const [codEntidad, rowId] = id.split('-');

    try {
      const token = await getAccessToken();
      await handleUpdateReportServer(codEntidad, rowId, reporte, newGroupValue, activo, token);
      return { ...newRow, isNew: false };
    } catch (error) {
      throw new Error('No se pudo actualizar el registro. Intenta de nuevo.');
    }
  };

  const handleOpenPopup = (row) => {
    setSelectedRow(row);
    setIsPopupOpen(true);
  };

  const columns = [
    { field: 'Reporte', headerName: 'Reporte', flex: 1 },
    { field: 'Descripcion', headerName: 'Descripción', flex: 1, valueGetter: (params) => params.row.Descripcion || 'Sin descripción' },
    { field: 'Group', headerName: 'Ruta', flex: 1 },
    {
      field: 'Activo',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) =>
        params.value === -1 ? <Chip label="Activo" color="success">Activo</Chip> : null,
    },
    {
      field: 'Acciones',
      headerName: 'Acciones',
      renderCell: (params) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <EditIcon
            className="MuiSvgIcon-root accions-icon"
            onClick={() => handleOpenPopup(params.row)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="grid-container">
      <Typography sx={{ fontSize: 14, color: 'gray' }}>CONFIGURACIÓN RUTA DE REPORT SERVER</Typography>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </div>
      ) : (
        <EscoDataGrid
          rows={reportes}
          columns={columns}
          pageSize={5}
          pAgregar={false}
          processRowUpdate={(newRow) => {
            console.log(newRow);
            return handleProcessRowUpdate(newRow);
          }}
          onProcessRowUpdateError={(error) => console.error('Error al actualizar:', error)}
          experimentalFeatures={{ newEditingApi: true }}
          classes={{
            columnHeaders: 'grid-header',
          }}
          autoHeight
        />
      )}
      <Snackbar
        open={alert.open}
        // autoHideDuration={20000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
          className="status-message"
        >
          {alert.message}
        </Alert>
      </Snackbar>
      {isPopupOpen && (
        <EditReportServer
          open={isPopupOpen}
          onClose={handleClosePopup}
          onSave={handleSavePopup}
          initialData={selectedRow}
        />
      )}
    </div>
  );
};

export default ConfigRutaReportServer;
